import { Box, Button, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { T } from 'GlobalStores/Cores/Translator/useTranslate'
import SessionAuth from 'GlobalStores/User/SessionAuth'
import storeAccountConflict from 'GlobalStores/User/StoreAccountConflict'

/**
 * Blocks the tab when its session was hijacked by another tab logging into a
 * different account (the token lives in shared localStorage).
 *
 * Method 1 — realtime: subscribes to the cross-tab `storage` event.
 * Safety re-checks on focus/visibility catch an override that happened while the
 * listener was not yet attached (e.g. the other tab logged in first).
 *
 * Reloading re-hydrates the tab against whatever account currently owns the
 * shared token, so the tab stops silently mixing the old account's UI with the
 * new account's token.
 */
const AccountConflictModal = observer(() => {
    useEffect(() => {
        const unsubscribe = SessionAuth.SubscribeAccountConflict(() => {
            storeAccountConflict.Trigger()
        })

        const recheck = () => {
            if (SessionAuth.GetTabIdentity() && !SessionAuth.IsTokenOwnedByThisTab()) {
                storeAccountConflict.Trigger()
            }
        }

        window.addEventListener('focus', recheck)
        document.addEventListener('visibilitychange', recheck)
        // Catch an override that already happened before this tab mounted.
        recheck()

        return () => {
            unsubscribe()
            window.removeEventListener('focus', recheck)
            document.removeEventListener('visibilitychange', recheck)
        }
    }, [])

    const handleReload = () => {
        window.location.reload()
    }

    if (!storeAccountConflict.IsConflict()) {
        return null
    }

    return (
        <Box
            role="dialog"
            aria-modal="true"
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 1300,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                fontFamily: 'Inter, NotoSansThai, sans-serif!important',
                boxSizing: 'border-box!important'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100%',
                    p: '16px'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: '343px',
                        width: '100%',
                        maxWidth: '420px',
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        padding: {
                            xs: '24px',
                            lg: '32px'
                        },
                        ':focus-visible': {
                            outline: 'none'
                        }
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#003B6F',
                            lineHeight: '25px',
                            mb: '8px',
                            fontFamily: 'Poppins, NotoSansThai, sans-serif!important'
                        }}
                    >
                        <T>account_conflict_title</T>
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                            color: '#003B6F',
                            lineHeight: '20px',
                            mb: '24px'
                        }}
                    >
                        <T>account_conflict_description</T>
                    </Typography>

                    <Button
                        variant="contained"
                        disableRipple
                        disableTouchRipple
                        disableFocusRipple
                        onClick={handleReload}
                        sx={{
                            fontFamily: 'Poppins, NotoSansThai, sans-serif!important',
                            fontSize: '18px',
                            color: '#FFFFFF',
                            fontWeight: '600',
                            height: '40px',
                            width: '100%',
                            borderRadius: '32px',
                            textTransform: 'none',
                            boxShadow: 'none',
                            backgroundColor: '#5A8FC3',
                            '&:hover': {
                                backgroundColor: '#5A8FC3',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        <T>account_conflict_reload</T>
                    </Button>
                </Box>
            </Box>
        </Box>
    )
})

export default AccountConflictModal
